import axios from 'axios'

class HistoryService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
   * @author Jorge Perales
   * @description Obtiene el catálogo de estatus de atención para el filtro de historial
   * @date 29/07/2026
   * @param {String} token
   * @returns {Object}
   * @memberof HistoryService
   */
  async getHistoryStatusCatalog(token) {
    let response = await axios.get(`${this.apiUrl}/history-status-catalog`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get conversations with messages and status metadata by clientId, year and month, limit and page
   * @date 29/07/2026
   * @param {String} year
   * @param {String} month
   * @param {String} token
   * @param {String} day
   * @param {String} page
   * @param {String} limit
   * @param {String} sort
   * @param {Object} [filters] Advanced search filters
   * @param {String} [filters.dateStart] Start date (YYYY-MM-DD)
   * @param {String} [filters.dateEnd] End date (YYYY-MM-DD)
   * @param {String} [filters.timeStart] Start time (HH:mm)
   * @param {String} [filters.timeEnd] End time (HH:mm)
   * @param {Array<String>} [filters.sentiments] Sentiment ids (positive|negative|neutral|unclassified)
   * @param {Array<String>} [filters.historyStatuses] Attention status ids (attended|not_attended|not_required|resolved)
   * @param {String} [filters.userId] Assigned user id
   * @returns {Object}
   * @memberof HistoryService
   */
  async getConversationsByDateAndPageLimit(year, month, token, day, botId, page, limit, sort, filters = {}) {
    const { dateStart, dateEnd, timeStart, timeEnd, sentiments, historyStatuses, userId } = filters

    let userTimezone
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      userTimezone = 'America/Mexico_City'
    }

    const sentimentQuery = Array.isArray(sentiments)
      ? sentiments.map(sentiment => '&sentiment=' + encodeURIComponent(sentiment)).join('')
      : ''

    const historyStatusQuery = Array.isArray(historyStatuses)
      ? historyStatuses.map(status => '&attentionStatus=' + encodeURIComponent(status)).join('')
      : ''

    let response = await axios.get(
      `${this.apiUrl}/conversations-with-metadata-by-page?year=${year}&month=${month}${day ? '&day=' + day : ''}${botId ? '&botId=' + botId : ''}${dateStart ? '&dateStart=' + encodeURIComponent(dateStart) : ''}${dateEnd ? '&dateEnd=' + encodeURIComponent(dateEnd) : ''}${timeStart ? '&timeStart=' + encodeURIComponent(timeStart) : ''}${timeEnd ? '&timeEnd=' + encodeURIComponent(timeEnd) : ''}${sentimentQuery}${historyStatusQuery}${userId ? '&userId=' + encodeURIComponent(userId) : ''}${page ? '&page=' + page : ''}${limit ? '&limit=' + limit : ''}${sort ? '&sort=' + sort : ''}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Timezone': userTimezone
        }
      }
    )
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Brayan Salas
   * @updatedBy Jorge Perales
   * @updatedDate 29/07/2026
   * @description Search conversations by matching message content
   * @param {String} search
   * @param {String} year
   * @param {String} month
   * @param {String} token
   * @param {String} day
   * @param {String} botId
   * @param {String} page
   * @param {String} limit
   * @param {String} sort
   * @param {Object} [filters] Advanced search filters
   * @param {String} [filters.dateStart] Start date (YYYY-MM-DD)
   * @param {String} [filters.dateEnd] End date (YYYY-MM-DD)
   * @param {String} [filters.timeStart] Start time (HH:mm)
   * @param {String} [filters.timeEnd] End time (HH:mm)
   * @param {Array<String>} [filters.sentiments] Sentiment ids (positive|negative|neutral|unclassified)
   * @param {Array<String>} [filters.historyStatuses] Attention status ids (attended|not_attended|not_required|resolved)
   * @param {String} [filters.userId] Assigned user id
   * @returns {Object}
   * @memberof HistoryService
   */
  async searchConversationsByMessage(search, year, month, token, day, botId, page, limit, sort, filters = {}) {
    const { dateStart, dateEnd, timeStart, timeEnd, sentiments, historyStatuses, userId } = filters

    let userTimezone
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      userTimezone = 'America/Mexico_City'
    }

    const sentimentQuery = Array.isArray(sentiments)
      ? sentiments.map(sentiment => '&sentiment=' + encodeURIComponent(sentiment)).join('')
      : ''

    const historyStatusQuery = Array.isArray(historyStatuses)
      ? historyStatuses.map(status => '&attentionStatus=' + encodeURIComponent(status)).join('')
      : ''

    let response = await axios.get(
      `${this.apiUrl}/search-conversations-by-message?search=${encodeURIComponent(search)}&year=${year}&month=${month}${day ? '&day=' + day : ''}${botId ? '&botId=' + botId : ''}${dateStart ? '&dateStart=' + encodeURIComponent(dateStart) : ''}${dateEnd ? '&dateEnd=' + encodeURIComponent(dateEnd) : ''}${timeStart ? '&timeStart=' + encodeURIComponent(timeStart) : ''}${timeEnd ? '&timeEnd=' + encodeURIComponent(timeEnd) : ''}${sentimentQuery}${historyStatusQuery}${userId ? '&userId=' + encodeURIComponent(userId) : ''}${page ? '&page=' + page : ''}${limit ? '&limit=' + limit : ''}${sort ? '&sort=' + sort : ''}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Timezone': userTimezone
        }
      }
    )

    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data.data
  }
}

export { HistoryService as default }
